import { appConfigs } from '@configs/apps/appConfigs';
import { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import type {
  InitSessionPayload,
  StartSessionPayload,
  StartExposureSessionPayload,
  StartPerformanceSessionPayload,
  ExposureItemIds,
  StopExperiencePayload,
  CancelExperiencePayload,
  ChangeEventPayload,
  TutorialCompletedPayload,
  ItemShownPayload,
  ItemFeedbackPayload,
  MealEndedPayload,
} from '@types';
import {
  normalizeInitSessionConfig,
  getExposureConfigFromSession,
} from './extractSessionObject';
import { getExposureItemIdsFromConfig } from './exposureMultiItem';

// Tipo per i parametri della sessione
export interface SessionParams {
  sessionId: string;
  deviceCode: string;
  scenario: string;
  appType: string;
}

// Tipo per la funzione sendMessage con overload per ogni evento
export type SendMessageFunction = {
  (eventType: 'initSession', payload: InitSessionPayload): void;
  (
    eventType: 'startSession',
    payload:
      | StartSessionPayload
      | StartExposureSessionPayload
      | StartPerformanceSessionPayload
  ): void;
  (eventType: 'stopSession', payload: StopExperiencePayload): void;
  // (eventType: 'stopExperience', payload: StopExperiencePayload): void; // TODO: Eliminare questo evento
  (eventType: 'cancelSession', payload: CancelExperiencePayload): void;
  (eventType: 'changeEvent', payload: ChangeEventPayload): void;
  (
    eventType: 'sessionStartedAt',
    payload: { deviceCode: string; sessionId: string; timestamp: string }
  ): void;
  (
    eventType: 'sessionEndedAt',
    payload: { deviceCode: string; sessionId: string; timestamp: string }
  ): void;
  (eventType: 'tutorialCompleted', payload: TutorialCompletedPayload): void;
  (eventType: 'itemShown', payload: ItemShownPayload): void;
  (eventType: 'itemFeedback', payload: ItemFeedbackPayload): void;
  (eventType: 'mealEnded', payload: MealEndedPayload): void;
  (eventType: string, payload: unknown): void;
};

export type SessionEventKind = 'exposure' | 'performance' | 'experience';

/**
 * Determina il kind di evento sessione da usare per init/start.
 */
export const getSessionEventKind = (appType: string): SessionEventKind => {
  if (appType === 'exposure') return 'exposure';
  if (appType === 'performance') return 'performance';
  return 'experience';
};

/**
 * Determina se l'app è di tipo bioFeedback (defaultVariation > 0).
 * Usato solo per change() (performance).
 */
export const isBioFeedbackApp = (appType: string): boolean => {
  const appConfig = appConfigs[appType] || appConfigs.default;
  return appConfig.defaultVariation > 0;
};

/**
 * Normalizza itemIds per il payload device (exposure).
 * Output sempre [] (nessun item) oppure [[],[],...] (array di portate) con id numerici.
 */
const normalizeItemIdsToMatrix = (
  itemIds: string[] | string[][] | number[] | number[][] | undefined
): ExposureItemIds => {
  if (!Array.isArray(itemIds) || itemIds.length === 0) return [];
  const first = itemIds[0];
  const toNumbers = (row: (string | number)[]): number[] =>
    row.map(id => (typeof id === 'number' ? id : Number(id)));
  if (Array.isArray(first)) {
    return (itemIds as (string | number)[][]).map(toNumbers);
  }
  return [toNumbers(itemIds as (string | number)[])];
};

/**
 * Crea la configurazione per initSession.
 * Per exposure usa la config column (itemIds, hasTutorial, isMultiItem); altrimenti gli step normalizzati.
 */
const createInitSessionConfig = (
  sessionId: string,
  currentExperience: DeviceConfigurationApi
): InitSessionPayload => {
  if (currentExperience.type === 'exposure') {
    const exposureConfig = getExposureConfigFromSession(currentExperience);
    return {
      sessionId,
      config: {
        config: exposureConfig,
      },
    };
  }
  const configSteps = normalizeInitSessionConfig(currentExperience.config);
  return {
    sessionId,
    config: {
      config: configSteps,
    },
  };
};

/**
 * Crea la configurazione per startSession (experience: payload generico).
 * Per experience non si invia la chiave map quando scenario è "-".
 */
const createStartSessionConfig = (
  deviceCode: string,
  scenario: string
): StartSessionPayload => {
  const payload: StartSessionPayload = {
    code: deviceCode,
    command: 'start',
  };
  if (scenario !== '-') {
    payload.map = scenario;
  }
  return payload;
};

/**
 * Builder per startSession exposure: code, command, map + userSpawnPosition, hasTutorial, isMultiItem, itemIds
 */
export const buildStartExposurePayload = (
  deviceCode: string,
  scenario: string,
  session: DeviceConfigurationApi
): StartExposureSessionPayload => {
  const exposure = getExposureConfigFromSession(session);
  const itemIds = normalizeItemIdsToMatrix(
    getExposureItemIdsFromConfig(exposure)
  );
  return {
    code: deviceCode,
    command: 'start',
    map: scenario,
    userSpawnPosition: exposure.userSpawnPosition ?? '1',
    hasTutorial: exposure.hasTutorial ?? false,
    isMultiItem: exposure.isMultiItem ?? false,
    itemIds,
  };
};

/**
 * Builder per startSession performance: code, command, map + phase (da config.currentState)
 */
export const buildStartPerformancePayload = (
  deviceCode: string,
  scenario: string,
  session: DeviceConfigurationApi
): StartPerformanceSessionPayload => {
  const config = session.config as { currentState?: string } | null | undefined;
  const phase = config?.currentState ?? 'p0';
  return {
    code: deviceCode,
    command: 'start',
    map: scenario,
    phase,
  };
};

/**
 * Crea la configurazione per stopSession
 */
const createStopSessionConfig = (
  deviceCode: string
): StopExperiencePayload => ({
  code: deviceCode,
  command: 'stop',
});

/**
 * Crea la configurazione per cancelSession
 */
const createCancelSessionConfig = (
  deviceCode: string
): CancelExperiencePayload => ({
  code: deviceCode,
  command: 'cancel',
});

/**
 * Crea la configurazione per changeEvent
 */
const createChangeEventConfig = (
  deviceCode: string,
  newState: string
): ChangeEventPayload => ({
  code: deviceCode,
  command: 'change',
  newState,
});

/**
 * Event Manager trasparente che gestisce automaticamente la logica bioFeedback
 */
export const createSessionEventManager = (
  appType: string,
  sessionId: string,
  currentExperience: DeviceConfigurationApi | null,
  sendMessage: SendMessageFunction
) => {
  const kind = getSessionEventKind(appType);
  const isBioFeedback = isBioFeedbackApp(appType);

  if (!currentExperience) {
    throw new Error('Current experience is required');
  }

  return {
    /**
     * Invia evento 'init': no-op per tutti i kind (baseline è solo UI; avvio reale su start).
     */
    init: () => {
      // Nessun evento init per exposure/performance/experience
    },

    /**
     * Invia evento 'start'
     * - experience: initSession (config step) + startSession (generico)
     * - exposure: solo startSession (StartExposureSessionPayload)
     * - performance: solo startSession (StartPerformanceSessionPayload)
     */
    start: () => {
      const { deviceCode, scenario } = currentExperience;
      switch (kind) {
        case 'experience': {
          const initConfig = createInitSessionConfig(
            sessionId,
            currentExperience
          );
          sendMessage('initSession', initConfig);
          const startConfig = createStartSessionConfig(deviceCode, scenario);
          sendMessage('startSession', startConfig);
          break;
        }
        case 'exposure': {
          const payload = buildStartExposurePayload(
            deviceCode,
            scenario,
            currentExperience
          );
          sendMessage('startSession', payload);
          break;
        }
        case 'performance': {
          const payload = buildStartPerformancePayload(
            deviceCode,
            scenario,
            currentExperience
          );
          sendMessage('startSession', payload);
          break;
        }
      }
    },

    /**
     * Invia evento 'stop'
     * - Sempre invia stopSession con command 'stop'
     */
    stop: () => {
      const config = createStopSessionConfig(currentExperience.deviceCode);
      sendMessage('stopSession', config);
      //sendMessage('stopExperience', config);
    },

    /**
     * Invia evento 'cancel'
     * - Sempre invia cancelSession con command 'cancel'
     */
    cancel: () => {
      const config = createCancelSessionConfig(currentExperience.deviceCode);
      sendMessage('cancelSession', config);
    },

    /**
     * Invia evento 'change'
     * - Se bioFeedback: invia changeEvent
     * - Se non bioFeedback: skip (non fa nulla)
     */
    change: (newState: string) => {
      if (isBioFeedback) {
        const config = createChangeEventConfig(
          currentExperience.deviceCode,
          newState
        );
        sendMessage('changeEvent', config);
      }
    },

    /**
     * Invia evento personalizzato
     * - Sempre invia l'evento (non dipende da bioFeedback)
     */
    customEvent: (eventType: string, payload: any) => {
      sendMessage(eventType, payload);
    },
  };
};

/**
 * Hook per utilizzare il SessionEventManager
 */
export const useSessionEventManager = (
  appType: string,
  sessionId: string,
  currentExperience: DeviceConfigurationApi | null,
  sendMessage: SendMessageFunction
) => {
  return createSessionEventManager(
    appType,
    sessionId,
    currentExperience,
    sendMessage
  );
};

/*
LIFE EXPERIENCE SESSION EVENTS


name: Connected clientId: 32-U5D44D connectionId: VILYaFzeBZ data: {"deviceCode":"32-U5D44D"}
18:37:35.307	Message
name: sessionEnded clientId: 32-U5D44D connectionId: VILYaFzeBZ data: {"deviceCode":"32-U5D44D","sessionId":"355","messages":["initial","A","Done (nel sneos che ha visto tutto il video)","D","05/12/2025 18:37:45"]}
18:36:58.942	Message
name: Connected clientId: 32-U5D44D connectionId: VILYaFzeBZ data: {"deviceCode":"32-U5D44D"}
18:36:33.624	Message
name: sessionStartedAt clientId: 32-U5D44D connectionId: VILYaFzeBZ data: {"deviceCode":"32-U5D44D","sessionId":"355","timestamp":"2025-12-05T18:36:43.7710926+01:00"}
18:36:33.562	Message
name: initSession clientId: web-900d7307-71cb-472b-9fd9-e1b48560ab40 connectionId: 4oIPHpwro4 data: {"sessionId":"355","config":{"config":[{"question":"Test","condition":"","answers":["A","B"]},{"video":"94","audio":"","condition":"A"},{"question":"asd","condition":"","answers":["C","D"]}]}}expand
18:36:08.948	Message
name: Connected clientId: 32-U5D44D connectionId: VILYaFzeBZ data: {"deviceCode":"32-U5D44D"}


name: sessionEnded clientId: 32-U5D44D connectionId: 2ORz5uw-sf data: {"deviceCode":"32-U5D44D","sessionId":"355","messages":["initial","skip","skip","D","05/12/2025 18:53:54"]}
18:53:44.008	Message
name: sessionInfo clientId: 32-U5D44D connectionId: 2ORz5uw-sf data: {"deviceCode":"32-U5D44D","sessionId":"355","messages":["step :2","answer:D"]}
18:53:37.447	Message
name: sessionInfo clientId: 32-U5D44D connectionId: 2ORz5uw-sf data: {"deviceCode":"32-U5D44D","sessionId":"355","messages":["step :1","answer:skip"]}
18:53:37.387	Message
name: sessionInfo clientId: 32-U5D44D connectionId: 2ORz5uw-sf data: {"deviceCode":"32-U5D44D","sessionId":"355","messages":["step :0","answer:B"]}






*/
